import React, { useEffect, useRef } from 'react';
import { LocalLibrary as LocalLibraryIcon } from '@material-ui/icons';

import Card from '../../../common/card/Card';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineDatePicker from '../../../common/inline/InlineDatePicker';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineHtml from '../../../common/inline/InlineHtml';
import InlineSelect from '../../../common/inline/InlineSelect';
import environment from '../../../environments';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateChangeOrder } from '../ChangeOrder.hook';
import {
  BaseChangeOrderCardProps,
  ChangeOrder
} from '../ChangeOrder.interface';

// quality change fields: yes, no, n/a
const qualityChangeSelectFields: { name: keyof ChangeOrder; label: string }[] =
  [
    {
      name: 'qualityChangeComponent',
      label:
        'Is the change a new or major change of operating system or any component?'
    },
    {
      name: 'qualityChangeArchitecture',
      label:
        'Is the change a new or modified architecture, database structure, or change of algorithm?'
    },
    {
      name: 'qualityChangeRequiredInput',
      label:
        'Does the change replace a required user input with a closed loop algorithm?'
    },
    {
      name: 'qualityChangeNewDiagnostic',
      label:
        'Does the change add a new diagnostic or therapeutic feature, or a new channel of interoperability?'
    },
    {
      name: 'qualityChangePresentation',
      label:
        'Does the change add or edit a user interface or presentation of medical data (new format or dimension of measuring unit)?'
    },
    {
      name: 'qualityChangeDataRequired',
      label:
        'Does the change require further clinical or usability data to support safety and performance?'
    },
    {
      name: 'qualityChangeRisks',
      label:
        'Does the change introduce new risks that require control measures or are existing risk control measures negatively affected?'
    },
    {
      name: 'qualityChangeIntendedUse',
      label:
        'Does the change alter the intended use of the device, including editing the intended use, changing the intended user or patient population, or changing the clinical use (i.e. anatomical site, access site or deployment methods)?'
    }
  ];

export interface ChangeOrderQualityDocumentCardProps
  extends BaseChangeOrderCardProps {
  changeOrder: ChangeOrder;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
}

export const ChangeOrderQualityDocumentCard: React.FC<
  ChangeOrderQualityDocumentCardProps
> = props => {
  const { changeOrder, onChange, setSuccessMessage, setErrorMessage } = props;
  const onChangeRef = useRef(onChange);
  const { updatedChangeOrder, isLoading, error, updateChangeOrder } =
    useUpdateChangeOrder(changeOrder.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(changeOrder.task.id || 0);
  const { isFieldDisabled } = useTaskEntityFields(changeOrder.task);
  const enableQualityChangeFields =
    environment.changeOrderConfig?.enableQualityChangeFields;

  const initialValues: Partial<ChangeOrder> = {
    triggeredPeriodicReview: changeOrder.triggeredPeriodicReview || '',
    qualityChangeComponent: changeOrder.qualityChangeComponent || '',
    qualityChangeArchitecture: changeOrder.qualityChangeArchitecture || '',
    qualityChangeRequiredInput: changeOrder.qualityChangeRequiredInput || '',
    qualityChangeNewDiagnostic: changeOrder.qualityChangeNewDiagnostic || '',
    qualityChangePresentation: changeOrder.qualityChangePresentation || '',
    qualityChangeDataRequired: changeOrder.qualityChangeDataRequired || '',
    qualityChangeRisks: changeOrder.qualityChangeRisks || '',
    qualityChangeIntendedUse: changeOrder.qualityChangeIntendedUse || '',
    documentInteraction: changeOrder.documentInteraction || '',
    regulationAssessment: changeOrder.regulationAssessment || '',
    commentsQualityDocument: changeOrder.commentsQualityDocument || '',
    qualityRegulatoryDueDate: changeOrder.qualityRegulatoryDueDate,
    qualityRegulatoryCompletedDate: changeOrder.qualityRegulatoryCompletedDate
  };

  useEffect(() => {
    if (updatedChangeOrder?.id) {
      setSuccessMessage('Change Order successfully updated');
      onChangeRef.current?.(updatedChangeOrder);
    }
  }, [setSuccessMessage, updatedChangeOrder]);

  useEffect(() => {
    if (Boolean(error || updateTaskError))
      setErrorMessage('There was an error saving');
  }, [setErrorMessage, error, updateTaskError]);

  useEffect(() => {
    if (updatedTask) {
      changeOrder.task = Object.assign(changeOrder.task || {}, updatedTask);
      onChange?.(changeOrder);
    }
  }, [updatedTask]);

  const handleOnChangeAttachment = (attachments: InlineFileValue[]) => {
    updateTask({ attachments });
    if (changeOrder.task) {
      changeOrder.task.attachments = attachments;
    }
    onChange?.(changeOrder);
  };

  return (
    <InlineFormik
      initialValues={initialValues}
      strict={false}
      isSubmitting={isLoading}
      error={error}
      onSubmit={(updatedValue: Partial<ChangeOrder>) => {
        const dto = { ...updatedValue };
        updateChangeOrder(dto);
      }}
    >
      <Card>
        <CardTitle>
          <LocalLibraryIcon />
          Quality/Regulatory Document Assessment
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            <InlineSelect
              name='triggeredPeriodicReview'
              label='Is this change for periodic review per Document Control SOP?'
              disabled={isFieldDisabled('triggeredPeriodicReview')}
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' }
              ]}
            />
            {enableQualityChangeFields &&
              qualityChangeSelectFields.map((field, index) => (
                <InlineSelect
                  key={index}
                  name={field.name}
                  label={field.label}
                  disabled={isFieldDisabled(field.name)}
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                    { value: 'n/a', label: 'N/A' }
                  ]}
                />
              ))}
            <InlineHtml
              name='documentInteraction'
              label='Interaction with other documents i.e. WI and Forms sync up with SOP'
              disabled={isFieldDisabled('documentInteraction')}
              attachments={changeOrder.task.attachments || []}
              onChangeAttachments={handleOnChangeAttachment}
            />
            <InlineHtml
              name='regulationAssessment'
              label='Assess change with respect to applicable regulations as needed'
              disabled={isFieldDisabled('regulationAssessment')}
              attachments={changeOrder.task.attachments || []}
              onChangeAttachments={handleOnChangeAttachment}
            />
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='qualityRegulatoryDueDate'
              label='Due Date'
              placeholder='Enter Due Date'
              disabled={isFieldDisabled('qualityRegulatoryDueDate')}
              autoCloseOnSelect
            />
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='qualityRegulatoryCompletedDate'
              label='Completed Date'
              placeholder='Enter Completed Date'
              disabled={isFieldDisabled('qualityRegulatoryCompletedDate')}
              autoCloseOnSelect
            />
            <InlineHtml
              name='commentsQualityDocument'
              label='Comments'
              disabled={isFieldDisabled('commentsQualityDocument')}
              helperText='Provide any additional information here'
              attachments={changeOrder.task.attachments || []}
              onChangeAttachments={handleOnChangeAttachment}
            />
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};
